import { createContext, useEffect, useRef } from "react";
import { AccessDataContext } from "types/access";
import { useState } from "react";
import { ICourse } from "@types";
import { SelectedStep } from "@types";
import { useContext } from "react";
import { KeyedMutator } from "swr";
import { useAppContext } from "@context/app";
import useCourseComments from "./useCourseComments";
import useCourseCards from "./useCourseCards";
import { VISITING_STATUS_LOCAL_STORAGE_KEY } from "@utils/constants";
import { useRouter } from "next/router";
import { PlayerElement } from "@modules/player";

interface Props {
  data: ICourse;
  mutate: KeyedMutator<ICourse>;
  children: React.ReactNode;
}

export const AccessContext = createContext<AccessDataContext>(
  {} as AccessDataContext,
);

const AccessProvider: React.FC<Props> = ({ children, data, mutate }) => {
  const {
    user: { isLoggedIn },
  } = useAppContext();
  const router = useRouter();

  const [loaded, setLoaded] = useState(false);
  const [courseData, setCourseData] = useState<ICourse>(data);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [stopAutoPlayLast, setStopAutoPlayLast] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [theaterMode, setTheaterMode] = useState(false);
  const [isExamResult, setIsResult] = useState(false);
  const [favCourseStatus, setFavCourseStatus] = useState<undefined | number>();

  const [isBottomExamVisible, setisBottomExamVisible] = useState(false);
  const [requestNext, setRequestNext] = useState(false);
  const [videoTime, setVideoTime] = useState(0);
  const [selectedLesson, setSelectedLesson] = useState<SelectedStep>({
    lessonIdx: 0,
    stepIdx: 0,
  });
  const [prevLesson, setPrevLesson] = useState<SelectedStep | null>(null);
  const {
    product: { sku, slug, id, attribute_type },
    user: { is_buy },
    original_lessons,
  } = data;
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [watchLessonCounter, setWatchLessonCounter] = useState(0);

  const [jumpVideoTime, setJumpVideoTime] = useState<number | null>(null);
  const [showNoteTab, setShowNoteTab] = useState<boolean>(false);

  const playerRef = useRef<PlayerElement | null>(null);
  const hasAccess = is_buy && isLoggedIn;

  const { comments, setComments } = useCourseComments(id);
  const {
    instructorPackage,
    instructorProduct,
    recentProduct,
    recommendedProduct,
    relatedPackage,
    relatedProduct,
    setInstructorPackage,
    setRecentProduct,
  } = useCourseCards({
    attribute_type,
    hasAccess,
    sku,
    slug,
  });

  useEffect(() => {
    const localStorageVal = localStorage.getItem(
      VISITING_STATUS_LOCAL_STORAGE_KEY,
    );
    const parsedLocalStorageVal = localStorageVal
      ? JSON.parse(localStorageVal)
      : null;
    const thisProductLocalStorageVal: {
      id: number;
      collection: number;
      lesson: number;
    } | null = Array.isArray(parsedLocalStorageVal)
      ? parsedLocalStorageVal.find((plv) => plv.id == id)
      : null;

    const cachedCollection =
      Number(router.query?.collection) ||
      thisProductLocalStorageVal?.collection;
    const lessonIndex = cachedCollection
      ? Math.max(cachedCollection - 1, 0)
      : null;

    const cachedLesson =
      Number(router.query?.lesson) || thisProductLocalStorageVal?.lesson;
    const stepIndex = cachedLesson ? Math.max(cachedLesson - 1, 0) : null;

    const isDefaulsSelectedLesson =
      !selectedLesson ||
      (selectedLesson.lessonIdx === 0 && selectedLesson.stepIdx === 0);

    const hasLessonIndex =
      typeof lessonIndex === "number" && !isNaN(lessonIndex);
    const hasStepIndex = typeof stepIndex === "number" && !isNaN(stepIndex);
    if (hasLessonIndex && hasStepIndex && isDefaulsSelectedLesson) {
      const isTheSame =
        selectedLesson.lessonIdx === lessonIndex &&
        selectedLesson.stepIdx === stepIndex;
      if (!isTheSame) {
        const isInvalidLesson = Boolean(
          original_lessons[0] && !original_lessons?.[lessonIndex],
        );
        const isInvalidStep = Boolean(
          original_lessons[lessonIndex] &&
            !original_lessons[lessonIndex].steps?.[stepIndex],
        );

        if (isInvalidLesson || isInvalidStep) {
          if (isInvalidLesson) router.query.collection = "1";
          router.query.lesson = "1";
          router.push(
            { pathname: router.pathname, query: router.query },
            undefined,
            { shallow: true },
          );
        }

        setSelectedLesson({
          lessonIdx: isInvalidLesson ? 0 : lessonIndex,
          stepIdx: Boolean(isInvalidStep || isInvalidLesson) ? 0 : stepIndex,
        });
      }
    }
  }, [router?.query, id]);

  useEffect(() => {
    setCourseData(data);
  }, [data]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("watchShowModalCounter");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const contextValue = {
    courseData,
    setCourseData,
    mutate,
    loaded,
    setLoaded,
    stopAutoPlayLast,
    setStopAutoPlayLast,
    scrolled,
    setScrolled,
    theaterMode,
    setTheaterMode,
    isExamResult,
    setIsResult,
    favCourseStatus,
    setFavCourseStatus,
    requestNext,
    setRequestNext,
    selectedLesson,
    setSelectedLesson,
    favoriteLoading,
    setFavoriteLoading,
    recentProduct,
    setRecentProduct,
    recommendedProduct,
    relatedProduct,
    instructorProduct,
    comments,
    setComments,
    prevLesson,
    setPrevLesson,
    videoTime,
    setVideoTime,
    instructorPackage,
    setInstructorPackage,
    relatedPackage,
    isSidebarOpen,
    setIsSidebarOpen,
    watchLessonCounter,
    setWatchLessonCounter,
    isBottomExamVisible,
    setisBottomExamVisible,
    jumpVideoTime,
    setJumpVideoTime,
    showNoteTab,
    setShowNoteTab,
    playerRef,
  };

  return (
    <AccessContext.Provider value={contextValue}>
      {children}
    </AccessContext.Provider>
  );
};

const useAccessContext = () => {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error("useAccessContext must be used within a AccessContext");
  }
  return context;
};

export { AccessProvider, useAccessContext };
